import { ParentProps, Show, createEffect, createMemo, createSignal, onMount } from 'solid-js'
import { exportForST, PrintRequest, toast, visitorManagementClient } from 'common'
import { visitorCheckin as api } from 'st-app/api'
import { requireVisitorCheckinContext, ReviewPayload, VisitorCheckinContext, VisitorCheckinContextJsx } from './VisitorCheckinContext'
import styles from './st-visitor-checkin.module.css'
import Wizard, { requireWizardContext } from '../st-wizard/st-wizard'
import { STComponentWrapperProps } from '../st-component-wrapper/st-component-wrapper'
import { PrintStep, VISITOR_TYPE_STAFF } from './fragments/print-step'
import { VerificationStep } from './fragments/verification-step'
import { ScanIDStep } from './fragments/scan-id-step'
import { StationRegistrationStep } from './fragments/station-registration-step'
import { MethodChooser } from './fragments/method-chooser'
import { ManualEntryStep } from './fragments/manual-entry-step'
import Button from '../st-button/st-button';
import { formatDob, formatName } from './fragments/util'


// Zero-based index of the "Verification" <Wizard.Step> declared below:
// 0 = Check In Method, 1 = Verification, 2 = Print. WizardContext.addStep() assigns
// each StepContext.id from the registration order of the Wizard.Step children, so this
// tracks the JSX order in VisitorCheckinInternal — keep them in sync.
const VERIFICATION_STEP_INDEX = 1;

// The wizard's props come from st-component-wrapper (REV_WEB_COMPONENT host), which
// passes the bound element's current value through as props.value — a plain string.
// Extending STComponentWrapperProps is what makes that visible to TypeScript; same
// pattern as st-document-reader's DocumentReaderProps.
export class VisitorCheckinProps extends STComponentWrapperProps { }

/**
 * Staff [Review] pushes a JSON payload (see ReviewPayload) into this component's bound
 * CheckInJson element; it reaches us as props.value. Any absent, non-JSON, or non-review
 * value returns null so the component falls through to normal Add-mode behaviour — an
 * ordinary walk-in check-in must never be affected by this path.
 */
export const parseReviewPayload = (raw: unknown): ReviewPayload | null => {
    try {
        let candidate: any = raw;

        // ST sometimes hands element values over wrapped in a CDATA envelope.
        if (candidate && typeof candidate === 'object' && '#cdata' in candidate) {
            candidate = candidate['#cdata'];
        }

        if (typeof candidate === 'string') {
            const trimmed = candidate.trim();
            if (!trimmed) return null;
            candidate = JSON.parse(trimmed);
        }

        if (!candidate || typeof candidate !== 'object') return null;

        return candidate.mode === 'review' && typeof candidate.vmVisitorGU === 'string' && candidate.vmVisitorGU
            ? candidate as ReviewPayload
            : null;
    } catch {
        // Malformed payload must degrade to normal Add mode, never crash or blank the wizard.
        return null;
    }
}

export default function VisitorCheckin(props: ParentProps<Omit<VisitorCheckinProps, 'componentName'>>) {
    // Read once, before the context is built: in review mode the visitor GU has to come
    // from the payload rather than being minted fresh.
    const context = new VisitorCheckinContext(parseReviewPayload(props.value)?.vmVisitorGU);

    return (
        <VisitorCheckinContextJsx.Provider value={context}>
            <VisitorCheckinInternal {...props} />
        </VisitorCheckinContextJsx.Provider>
    )
}

/**
 * Review mode opens straight on Verification. WizardContext seeds currStep from
 * initialStep but each StepContext.completed signal independently defaults to false, and
 * ControlPanel gates Next on the *current* step's completed flag — so staff who pressed
 * Previous to look at something would find Next disabled and be stranded. Mark the
 * skipped steps complete through StepContext.setCompleted, the same mechanism the
 * individual steps use when they advance themselves.
 */
function CompleteStepsBefore(props: { index: number }) {
    const wizard = requireWizardContext();

    createEffect(() => {
        // Tracks steps() because Wizard.Step children register themselves as they render.
        // Re-setting an already-true signal is a no-op, so this cannot loop.
        for (const step of wizard.steps()) {
            if (step.id < props.index) step.setCompleted(true);
        }
    });

    return null;
}

/**
 * There is no check-in method to choose in review mode — the kiosk already captured the
 * ID. Show a read-only recap so backing up out of Verification does not land staff on an
 * empty manual-entry form (whose Continue would create a second visitor record).
 */
const ReviewSummary = () => {
    const checkin = requireVisitorCheckinContext();

    return (
        <div style="display:flex; gap:16px; align-items:center; padding:16px;">
            <Show when={checkin.scan()?.photoBase64}>
                <img style="height:120px; border-radius:8px;" src={`data:image/bmp;base64,${checkin.scan()!.photoBase64}`} alt="Visitor photo" />
            </Show>
            <div style="display:flex; flex-direction:column; gap:4px;">
                <span style="font-weight:bold; font-size:1.2em;">{formatName(checkin.scan()?.AAMVA)}</span>
                <span>Date of Birth: {formatDob(checkin.scan()?.AAMVA?.dateOfBirth) || 'N/A'}</span>
                <span>ID Number: {checkin.scan()?.AAMVA?.idNumber || 'N/A'}</span>
                <span style="opacity:0.8; font-style:italic;">Captured at the kiosk — flagged for staff review.</span>
            </div>
        </div>
    );
}

function VisitorCheckinInternal(props: ParentProps<Omit<VisitorCheckinProps, 'componentName'>>) {
    const checkin = requireVisitorCheckinContext();
    const reviewPayload = createMemo(() => parseReviewPayload(props.value));

    // props.value is a signal fed by the ST data-binding layer (STWebComponent's
    // UpdateInputValue), so it can land after mount — hydrate from an effect rather than
    // onMount, and only once.
    let hydrated = false;
    createEffect(() => {
        const payload = reviewPayload();
        if (!payload || hydrated) return;

        hydrated = true;
        checkin.hydrateForReview(payload);
    });

    const isReviewMode = createMemo(() => checkin.entryMode() === 'review');
    const [isSubmitting, setIsSubmitting] = createSignal(false);
    const [stationReady, setStationReady] = createSignal(false);

    const bridgeStatusMessage = createMemo(() => {
        const status = checkin.vmBridgeStatus();
        if (!status) return null;
        if (status.status === 'not_running') return 'Visitor management bridge is not running. Please ensure the service is started and try again.';
        if (status.status === 'outdated') return `Visitor management bridge is outdated (${status.currentVersion} → ${status.latestVersion}). Please update the bridge.`;
        return null;
    });

    onMount(async () => {
        const status = await visitorManagementClient.getStatus();
        checkin.setVmBridgeStatus(status);

        if (status.status !== 'running') return;

        const machineInfo = await visitorManagementClient.getMachineInfo();
        checkin.setMachineInfo(machineInfo);

        if (checkin.vmStationGU()) {
            setStationReady(true);
            return;
        }

        const result = await api.CheckStationRegistration(machineInfo.macAddress);
        if (result.isRegistered && result.vmStationGU) {
            checkin.setVmStationGU(result.vmStationGU);
            setStationReady(true);
        }
    });

    const submit = async () => {
        if (isSubmitting())
            return;

        setIsSubmitting(true);
        try {
            if (!checkin.vmGU()) {
                toast.notify('error', 'Visitor ID is missing. Please go back to the Scan ID step and ensure the ID was scanned properly.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.verificationStatus() || checkin.verificationStatus() === 'pending') {
                toast.notify('error', 'Visitor verification is not complete. Please go back to the Verification step and ensure the visitor has been approved.');
                setIsSubmitting(false);
                return;
            }

            if (checkin.verificationStatus() === 'dangerous') {
                toast.notify('error', 'The visitor has been flagged as dangerous and cannot be checked in. Please go back to the Verification step.');
                setIsSubmitting(false);
                return;
            }

            // 'review' is an UNRESOLVED Warn-tier watch-list match: screening produced a possible
            // hit that no one has confirmed or dismissed yet. It used to fall through to
            // CompleteCheckin below, which admitted exactly the visitor this feature exists to
            // stop. 'error' means screening could not be completed at all, so it fails closed for
            // the same reason. Both are cleared only by an explicit staff resolution on the
            // Verification step, which sets 'verified'.
            //
            // NOTE: this client-side VerificationStatus 'review' is NOT the persisted
            // EPC_VM_VISITOR.STATUS code 'REVW' (VisitorStatus). Different things, both present in
            // this component — do not unify them.
            if (checkin.verificationStatus() === 'review' || checkin.verificationStatus() === 'error') {
                toast.notify('error', 'This visitor\'s screening has not been resolved. Please go back to the Verification step and confirm or dismiss the match.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.reason()) {
                toast.notify('error', 'Check-in reason is required. Please go back to the Print step and select a reason for the visit.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.visitorType()) {
                toast.notify('error', 'Visitor type is required. Please go back to the Print step and select a visitor type.');
                setIsSubmitting(false);
                return;
            }

            if (checkin.reason() === '6' && !checkin.notes()) {
                toast.notify('error', 'Notes are required when "Other" is selected as the reason for the visit. Please go back to the Print step and enter notes describing the reason for the visit.');
                setIsSubmitting(false);
                return;
            }

            const cellPhone = checkin.cellPhone();
            if (cellPhone && cellPhone.trim()) {
                const digits = cellPhone.replace(/\D/g, '');
                if (digits.length !== 10) {
                    toast.notify('error', 'Cell phone number must be 10 digits. Please enter a valid US phone number (e.g. 555-555-5555).');
                    setIsSubmitting(false);
                    return;
                }
            }

            const email = checkin.email();
            if (email && email.trim()) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
                    toast.notify('error', 'Email address is not valid. Please enter a valid email address (e.g. name@example.com).');
                    setIsSubmitting(false);
                    return;
                }
            }

            // THE single durable write for a review resolution, and therefore the place the
            // concurrency pre-check has to run. The resolve buttons on the Verification step only
            // claim the row and advance; on the 'clean' route there is no resolve button at all and
            // this is the only write in the whole flow — so without this check the most common
            // review outcome would be completely unguarded and a second tab could silently
            // overwrite the first tab's decision.
            //
            // Same shared implementation the resolve buttons use (VisitorCheckinContext), which
            // returns 'ok' with no round trip outside review mode.
            if (checkin.entryMode() === 'review') {
                const claim = await checkin.claimReviewRow();

                if (claim === 'check-failed') {
                    // Row state unknown: write nothing, but keep the wizard open with the operator's
                    // edits intact and say plainly that nothing was saved.
                    toast.notify('error', 'Could not confirm this row is still awaiting review — nothing was saved. Check your connection and try again.');
                    setIsSubmitting(false);
                    return;
                }

                if (claim === 'already-resolved') {
                    toast.notify('notice', 'This visitor was already resolved by another user.');
                    if (import.meta.env.DEV) {
                        toast.notify('notice', '(DEV mode, modal not closed)');
                    } else if (window.ST) {
                        window.ST.CloseModal();
                        window.ST.RefreshView();
                    }
                    setIsSubmitting(false);
                    return;
                }
            }

            await api.CompleteCheckin({
                cellPhone: checkin.cellPhone(),
                email: checkin.email(),
                checkInReason: checkin.reason(),
                meetingWith: checkin.meetingWith(),
                notes: checkin.notes(),
                visitorGU: checkin.vmGU()!,
                visitorType: checkin.visitorType(),
                vmStationGU: checkin.vmStationGU(),
            })

            // AWAIT the print. It used to be fire-and-forget, so the modal tore down before the
            // outcome was known and the operator's only feedback was an unconditional success toast.
            //
            // A print failure must NOT undo the check-in: CompleteCheckin above has already recorded
            // ALLOW and this visitor is admitted. checkin.print() reports its own failure (with the
            // bridge's message) and never throws, so nothing here can reach the catch below and
            // mislabel a print failure as "the check-in failed". We still close the modal on failure —
            // re-submitting would write a second CompleteCheckin, so the intended retry path is the
            // grid's Reprint button. The error toast is raised at document level (outside the modal),
            // so it outlives CloseModal/RefreshView.
            //
            // Staff never get a printed badge — they already carry one, and that is the whole point
            // of the Staff visitor type. This applies in BOTH review mode (the kiosk-flagged staff
            // badge path) and normal Add mode, where an operator can pick "Staff" from the Visitor
            // Type dropdown on an ordinary walk-in; print-step.tsx's isStaffRow() shows "No badge
            // needed" in that same case so the UI does not silently do nothing. The check-in itself
            // (CompleteCheckin above) is unaffected — only this print call is skipped.
            if (checkin.visitorType() !== VISITOR_TYPE_STAFF && checkin.selectedPrinterName()) {
                await checkin.print();
            }

            if (import.meta.env.DEV) {
                toast.notify('success', 'Visitor check-in completed successfully. (DEV mode, modal not closed)');
            } else {
                if (window.ST) {
                    window.ST.CloseModal();
                    window.ST.RefreshView();
                }
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while submitting the visitor check-in.');
        }

        setIsSubmitting(false);
    }

    /**
     * Tripwire for the initialStep ordering dependency documented at the station gate below.
     * Rendered as a null-returning child immediately before <Wizard>, so it runs at exactly the
     * moment WizardContext is about to read initialStep once and for all.
     *
     * A parsed review payload with entryMode() still unset at that moment means hydration lost the
     * race and the wizard is about to open on the Check In Method step instead of Verification.
     * Deliberately NOT gated on import.meta.env.DEV: the failure is a timing race that only shows
     * up in a real deployment, and a console.error costs nothing and changes no behaviour.
     */
    const assertReviewHydratedBeforeWizard = () => {
        if (reviewPayload() && checkin.entryMode() !== 'review') {
            console.error(
                '[st-visitor-checkin] A [Review] payload was parsed but entryMode() is not "review" '
                + 'at Wizard construction. initialStep is read once and non-reactively, so the wizard '
                + 'will open on the wrong step. The hydration effect must run before the '
                + 'vmBridgeStatus/stationReady gates release — see the comment at those gates.');
        }
        return null;
    }

    const ensureVerified = async (): Promise<boolean> => {
        if (checkin.isVerifying()) return false;

        if (checkin.verificationStatus() === 'dangerous') {
            return false;
        }

        // Same fail-closed rule as submit(): an unresolved Warn-tier match ('review'), a screening
        // that could not be completed ('error'), or a screening that has not produced a verdict yet
        // ('pending') must not be able to step forward to Print. See the longer note in submit(),
        // which refuses the same three.
        if (checkin.verificationStatus() === 'review'
            || checkin.verificationStatus() === 'error'
            || checkin.verificationStatus() === 'pending') {
            return false;
        }

        return true;
    }

    return (
        <div class={styles['st-visitor-checkin']}>
            <Show when={bridgeStatusMessage()}>
                {(msg) => (
                    <div style="padding: 16px; display: flex; flex-direction: column; gap: 8px; align-items: flex-start;">
                        <span style="color: var(--st-danger-color);">{msg()}</span>
                        <Button icon='fas fa-download'>
                            <a download="" href={'dist/SynergyVisitorManagementBridge-Setup.exe'}>Download Latest Installer</a>
                        </Button>
                    </div>
                )}
            </Show>
            {/*
              * ORDERING DEPENDENCY, read this before restructuring the two gates below.
              *
              * <Wizard>'s initialStep is read ONCE and non-reactively by WizardContext's
              * constructor. It is keyed off entryMode() === 'review', which the hydration effect
              * above sets. That works only because these two gates are asynchronous — the bridge
              * status and stationReady() both resolve inside the awaited onMount — so the Wizard
              * is not constructed until well after the synchronous hydration effect has run.
              *
              * That ordering is incidental, not designed. If either gate ever becomes synchronous
              * (cached bridge status, pre-registered station, a fallback that renders the Wizard
              * immediately), the Wizard can be constructed while entryMode() is still undefined
              * and review mode will silently open on step 0 instead of Verification. The tripwire
              * just inside the gate catches exactly that.
              */}
            <Show when={checkin.vmBridgeStatus()?.status === 'running'}>
                <Show when={stationReady()} fallback={<StationRegistrationStep onRegistered={() => setStationReady(true)} />}>
                    {assertReviewHydratedBeforeWizard()}
                    <Wizard onComplete={submit} size='small' initialStep={isReviewMode() ? VERIFICATION_STEP_INDEX : 0}>
                        <Wizard.Step title="Check In Method">
                            <Show when={isReviewMode()}>
                                <ReviewSummary />
                            </Show>
                            <Show when={!isReviewMode()}>
                                <Show when={checkin.entryMode()} fallback={<MethodChooser />}>
                                    <div style="display:flex; justify-content:flex-end; padding:0 8px 8px;">
                                        <a href="#" onClick={(e) => { e.preventDefault(); checkin.onScan(undefined); checkin.setEntryMode(undefined); }}>
                                            ← change method
                                        </a>
                                    </div>
                                    <Show when={checkin.entryMode() === 'scan'} fallback={<ManualEntryStep />}>
                                        <ScanIDStep />
                                    </Show>
                                </Show>
                            </Show>
                        </Wizard.Step>

                        <Wizard.Step title="Verification" onNext={ensureVerified}>
                            <VerificationStep />
                        </Wizard.Step>

                        <Wizard.Step title="Print">
                            <PrintStep />
                        </Wizard.Step>

                        <Show when={isReviewMode()}>
                            <CompleteStepsBefore index={VERIFICATION_STEP_INDEX} />
                        </Show>
                    </Wizard>
                </Show>
            </Show>
        </div>
    );
}


VisitorCheckin.defaultProps = new VisitorCheckinProps();

async function printVisitorBadge(visitorGU: string, printerName?: string) {
    try {
        const preferredPrinter = localStorage.getItem('st-visitor-checkin.preferred-printer') || '';
        const printData = await api.GetBadgePrintData(visitorGU);
        const request: PrintRequest = {
            PrinterName: printerName || preferredPrinter,
            Data: {
                GUID: printData.visitorGU!,
                CheckInDateTime: printData.checkInDateTime!,
                FirstName: printData.firstName!,
                LastName: printData.lastName!,
                ImageBase64: printData.photoBase64 || '',
                MeetingWith: printData.meetingWith || '',
                Reason: printData.reason || '',
                VisitorBarCode: printData.visitorGU!,
                VisitorType: printData.visitorType || '',
            }
        }
        // print() now returns { success, message } instead of a bare response.ok. The guard below
        // used to be dead code: the bridge answered "Printer not found" with HTTP 200, so response.ok
        // was always true and this path reported success for a badge that never printed.
        const result = await visitorManagementClient.print(request);
        if (!result.success) {
            toast.notify('error', result.message
                ? `Failed to print visitor badge: ${result.message}`
                : 'Failed to print visitor badge. Please check the printer and try again.');
            return;
        }

        toast.notify('success', 'Visitor badge printed successfully.');
    } catch (e) {
        console.error(e);
        toast.notify('error', 'An error occurred while printing the visitor badge.');
    }
}

exportForST({
    Actions: {
        VisitorManagement: {
            PrintVisitorBadge: (params: {
                vmVisitorGU: string | {
                    '#cdata': string
                }
            }) => {
                const visitorGU = typeof params.vmVisitorGU === 'string' ? params.vmVisitorGU : params.vmVisitorGU['#cdata'];
                printVisitorBadge(visitorGU);
            },
        }
    }
});